import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { testimonials, communityStats } from "@/mocks/community";
import ContactForm from "./components/ContactForm";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "只做校园话题",
    text: "不做泛娱乐内容，所有帖子都围绕高校场景筛选，从选课、宿舍到校招，都是学生真实会遇到的事。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "按双维度组织",
    text: "既有主题板块，也能按学校筛选。你可以只看本校的现场，也可以横向对比不同学校处理同一件事的方式。",
  },
  {
    icon: "ri-time-line",
    title: "强调完整脉络",
    text: "热点事件由编辑补充分条更新，把时间线、后续处理与结论整理清楚，而不是只给你一句道听途说。",
  },
];

const principles = [
  {
    title: "不披露可识别个人的隐私信息",
    text: "真实姓名、寝室号、联系方式与清晰可辨的照片都不予展示。讨论事件本身没问题，但方向不该是对某个人的围攻。",
  },
  {
    title: "区分事实与观点",
    text: "热点帖会尽可能标注信息状态：哪些来自公开通知，哪些是当事人自述，哪些仍是未经证实的说法。",
  },
  {
    title: "保留更正与下架通道",
    text: "如果内容与事实不符，当事人或知情者都可以通过联系页面反馈，核实后我们会补充说明或直接下架。",
  },
  {
    title: "拒绝抄袭与搬运",
    text: "搬运他人平台的内容需经过授权并注明来源，我们更鼓励各校区贡献者写出自己观察到的第一手细节。",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 - 高校吃瓜网",
      description:
        "了解高校吃瓜网的品牌介绍、网站定位、内容原则、用户口碑与联系方式。",
      url: "https://www.gaoxiaochigua.cn/about",
    },
    {
      "@type": "Organization",
      name: "高校吃瓜网",
      url: "https://www.gaoxiaochigua.cn/",
      email: "contact@gaoxiaochigua.cn",
      description: "面向在校学生的校园话题聚合与讨论社区。",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.gaoxiaochigua.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://www.gaoxiaochigua.cn/about" },
      ],
    },
  ],
};

export default function About() {
  return (
    <SiteLayout>
      <PageMeta
        title="关于我们 - 高校吃瓜网 | 品牌介绍与内容原则"
        description="高校吃瓜网是面向在校学生的校园话题聚合与讨论社区。了解我们的网站定位、内容原则、用户口碑与联系方式，一起维护理性的讨论环境。"
        keywords="关于高校吃瓜网,高校吃瓜社区介绍,校园话题聚合,社区内容原则"
        path="/about"
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </PageMeta>

      <div className="mx-auto w-full px-4 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12">
        <Breadcrumbs items={[{ label: "关于我们" }]} />

        <header className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-14">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-plant-line"></i>
              </span>
              关于我们
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[44px]">
              让校园里发生的事
              <br />
              被完整地看见
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              高校吃瓜网做的事情很朴素：把散落在各个校园群、校园墙和讨论区里的信息，整理成能读得下去的内容。我们相信，比起道听途说的一句传闻，一条有时间线、有后续、有出处的讨论更值得被留下。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/topics"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-compass-3-line text-base"></i>
                开始逛讨论区
              </Link>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-md border border-foreground-300 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
              >
                <i className="ri-mail-line text-base"></i>
                联系我们
              </a>
            </div>
          </div>

          <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20flat%20illustration%20of%20a%20team%20editing%20campus%20stories%20on%20boards%20with%20pinned%20notes%20and%20timelines%2C%20warm%20coral%20terracotta%20amber%20and%20lime%20palette%2C%20minimal%20geometric%20shapes%2C%20clean%20cream%20background&width=1000&height=800&seq=campus-about-2026&orientation=landscape"
              alt="高校吃瓜网编辑团队整理校园话题信息的工作场景插画"
              title="高校吃瓜网品牌介绍"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </header>

        <section aria-labelledby="who-title" className="mt-16 md:mt-24">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <div>
              <h2
                id="who-title"
                className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]"
              >
                我们是谁
              </h2>
              <p className="mt-4 text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                高校吃瓜网由一群已经毕业和还在读书的年轻人一起维护。我们中有做内容编辑的，有在各校区帮忙整理线索的贡献者，也有负责技术维护的同学。大家之所以凑在一起，是因为都经历过这样一件事：学校发生了一件挺重要的事，但信息在几十个群里传来传去，最后谁也不知道完整版本长什么样。
              </p>
              <p className="mt-4 text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                所以这个站点从第一天起就把「整理」当成主要工作。我们不生产八卦，我们把已经存在的讨论梳理清楚，让后来的同学几分钟就能读完。到目前为止，讨论来自 1280 多所高校，每天新增三千多条帖子，背后是几百位愿意分享的同学。
              </p>
            </div>

            <dl className="grid grid-cols-2 gap-4">
              {communityStats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`rounded-lg border p-5 ${
                    index % 3 === 0
                      ? "border-primary-200 bg-primary-50"
                      : index % 3 === 1
                        ? "border-accent-200 bg-accent-50"
                        : "border-secondary-200 bg-secondary-50"
                  }`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-foreground-800">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <dd className="mt-3.5 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    {stat.value}
                  </dd>
                  <dt className="mt-0.5 text-xs text-foreground-600 md:text-sm">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section aria-labelledby="position-title" className="mt-16 md:mt-24">
          <h2
            id="position-title"
            className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]"
          >
            网站定位与发展理念
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            我们不想把它做成一个流量至上的内容农场。相比把标题写得更刺激，我们更在意一件事：读完这条帖子，你是否真的了解了事情的来龙去脉。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {positioning.map((item) => (
              <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="principles"
          aria-labelledby="principles-title"
          className="mt-16 scroll-mt-24 md:mt-24"
        >
          <div className="rounded-lg bg-background-100 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-shield-check-line"></i>
              </span>
              内容原则
            </span>
            <h2
              id="principles-title"
              className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]"
            >
              我们坚持的四条底线
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-7 md:grid-cols-2">
              {principles.map((item, index) => (
                <article key={item.title} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-foreground-950 font-heading text-sm font-black text-background-50">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="voice" aria-labelledby="voice-title" className="mt-16 scroll-mt-24 md:mt-24">
          <h2
            id="voice-title"
            className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]"
          >
            用户口碑
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            下面是几位长期使用者的真实反馈，他们来自不同的学校、不同的年级，关注的点也各不相同。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-6 w-6 items-center justify-center text-secondary-500">
                  <i className="ri-double-quotes-l text-xl"></i>
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-user-smile-line"></i>
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-bold text-foreground-950">
                      {item.name}
                    </span>
                    <span className="block text-xs text-foreground-500">{item.school}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" aria-labelledby="contact-title" className="mt-16 scroll-mt-24 md:mt-24">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
            <div>
              <h2
                id="contact-title"
                className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]"
              >
                联系我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                无论是投稿话题、反馈错误、申请成为校区贡献者，还是希望洽谈内容合作，都可以通过下面的表单告诉我们。我们通常会在三个工作日内回复。
              </p>

              <ul className="mt-6 flex flex-col gap-4">
                <li className="flex items-center gap-3.5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">邮箱</span>
                    <span className="block text-sm text-foreground-900">contact@gaoxiaochigua.cn</span>
                  </span>
                </li>
                <li className="flex items-center gap-3.5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-wechat-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">投稿方式</span>
                    <span className="block text-sm text-foreground-900">
                      通过表单提交，或联系你所在校区的贡献者
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-3.5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">回复时间</span>
                    <span className="block text-sm text-foreground-900">工作日 10:00 - 19:00</span>
                  </span>
                </li>
              </ul>
            </div>

            <ContactForm />
          </div>
        </section>

        <section className="mt-16 rounded-lg bg-primary-950 px-6 py-12 text-center md:mt-24 md:px-12 md:py-16">
          <h2 className="font-heading text-2xl font-black tracking-tight text-background-50 md:text-[32px]">
            来讨论区看看今天的校园
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-primary-200 md:text-[15px]">
            每天有三千多条新帖子在更新，也许你学校的那条讨论，正好就在榜上。
          </p>
          <Link
            to="/topics"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-secondary-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-secondary-400 whitespace-nowrap"
          >
            <i className="ri-compass-3-line text-base"></i>
            进入讨论区
          </Link>
        </section>
      </div>
    </SiteLayout>
  );
}